import type { User } from 'c-sdk';

/** Sorting and search for the buyer page's users tab (all client side). */

export type BuyerUsersOrderBy = 'id' | 'name' | 'email' | null;
export type BuyerUsersOrder = 'asc' | 'desc';

/** Clicking the active column flips its direction; another column starts ascending. */
export const nextBuyerUsersSort = (
    current: { orderBy: BuyerUsersOrderBy; order: BuyerUsersOrder },
    field: BuyerUsersOrderBy,
) => {
    if (!field) return current;
    if (current.orderBy === field)
        return { orderBy: field, order: current.order === 'asc' ? 'desc' : 'asc' } as const;
    return { orderBy: field, order: 'asc' } as const;
};

/** Name or email contains the trimmed term, ignoring case. */
export const filterBuyerUsers = (users: User[], search: string) => {
    const term = search.trim().toLowerCase();
    if (!term) return users;

    return users.filter(u => {
        const name = u.name ?? '';
        const email = u.email ?? '';
        return name.toLowerCase().includes(term) || email.toLowerCase().includes(term);
    });
};

/** Missing values sort first ascending; values compare as lower-case text. */
export const sortBuyerUsers = (
    users: User[],
    orderBy: BuyerUsersOrderBy,
    order: BuyerUsersOrder,
) => {
    if (!orderBy) return users;

    return [...users].sort((a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];

        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return order === 'asc' ? -1 : 1;
        if (bVal == null) return order === 'asc' ? 1 : -1;

        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();

        if (aStr < bStr) return order === 'asc' ? -1 : 1;
        if (aStr > bStr) return order === 'asc' ? 1 : -1;
        return 0;
    });
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** The text split into pieces, flagging the ones that match the search (for bolding). */
export const highlightParts = (text: string | null | undefined, search: string) => {
    const term = search.trim();
    if (!text || !term) return null;

    const regex = new RegExp(`(${escapeRegExp(term)})`, 'ig');
    return text.split(regex).map(part => ({ part, match: regex.test(part) }));
};
